import axios from 'axios';
import global from '../config/Global.json';

class APIClient {
  constructor() {
    this.baseURL = global.api.host;
    this.token = null;
    this.endpoints = {
      auth: '/auth/token/',
      groups: '/api/v1/migrateGroups',
      drvGroups: '/api/v1/migrateDrvGroups',
      users: '/api/v1/migrateUsers',
      transactions: '/api/v1/migrateTransactions',
      subscriptions: '/api/v1/migrateSubscriptions',
      download: '/api/v1/exportToCsv',
      verifyTransactions: '/api/v1/verifyMigratedTransactions/'
    };
  }

  getToken() {
    const authData = localStorage.getItem('authToken');
    if (authData) {
      const parsed = JSON.parse(authData);
      this.token = parsed.access;
      return true;
    }
    throw new Error('No auth token found in localStorage');
  }

  async uploadBatch(batchData, endpoint, retryCount = 0, confirmExisting = false) {
    console.log('📤 uploadBatch called:', { endpoint, batchSize: batchData.size, retryCount });
    try {
      if (!this.token) this.getToken();

      const formData = new FormData();
      formData.append('file', batchData, 'batch.csv');
      if (confirmExisting) formData.append('confirm_existing', 'true');
      console.log('📦 FormData prepared, sending to:', `${this.baseURL}${endpoint}`);

      const response = await axios.post(`${this.baseURL}${endpoint}`, formData, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('✅ Backend response:', response.status, response.data);

      // Refresh token on every successful response for long operations
      if (response.status === 200 || response.status === 201) {
        try {
          await this.refreshToken();
        } catch (refreshError) {
          // If refresh fails, continue with current token
          console.warn('Token refresh failed, continuing with current token:', refreshError.message);
        }
      }

      return response.data;
    } catch (error) {
      console.error('❌ Upload failed:', error.response?.status, error.response?.data || error.message);
      // Handle token refresh on 401 error
      if (error.response?.status === 401 && retryCount < 2) {
        try {
          await this.refreshToken();
          return this.uploadBatch(batchData, endpoint, retryCount + 1);
        } catch (refreshError) {
          throw new Error('Authentication failed after token refresh');
        }
      }
      throw error;
    }
  }

  async refreshToken() {
    try {
      // Always get fresh data from localStorage
      const authData = localStorage.getItem('authToken');
      if (!authData) throw new Error('No refresh token available');
      
      const currentTokenData = JSON.parse(authData);
      const { refresh } = currentTokenData;
      
      const response = await axios.post(`${this.baseURL}/auth/token/refresh/`, { refresh });
      
      // Update with new tokens (access and refresh if provided)
      const newTokenData = {
        ...currentTokenData,
        access: response.data.access,
        refresh: response.data.refresh || currentTokenData.refresh
      };
      
      localStorage.setItem('authToken', JSON.stringify(newTokenData));
      this.token = response.data.access;
      
      // Trigger a storage event to update AuthContext across the app
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'authToken',
        newValue: JSON.stringify(newTokenData)
      }));
      
      console.log('Token refreshed successfully for CSV migration');
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }
}

export default new APIClient();
